import { useCallback, useEffect, useRef, useState } from 'react';
import type { WordConnectPlayState } from '../services/api';
import { decryptWordConnectBootstrapCipher } from '../utils/wordConnectBootstrapDecrypt';
import { parseWordConnectPlayState } from '../utils/wordConnectQuizFormat';
import {
  loadWordConnectAssemblyExports,
  type WordConnectWasmExports,
} from '../utils/wordConnectWasmExports';

export interface WordConnectFinishAuditPayload {
  playSessionId: string;
  quizId: string;
  placements: Array<{
    slotId: string;
    row: number;
    col: number;
    timestampUtcMs: number;
  }>;
  clientTotalScore: number;
  pointsPerWordSlot: number[];
  signature: string;
}

const WASM_BASE =
  (process.env.VITE_WORD_CONNECT_WASM_BASE ?? '/wasm/word-connect').replace(/\/$/, '');

let dotnetModulePromise: Promise<{
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
  getConfig: () => { mainAssemblyName: string };
}> | null = null;

async function loadWasmExports(): Promise<WordConnectWasmExports> {
  if (!dotnetModulePromise) {
    dotnetModulePromise = (async () => {
      const dotnetUrl = `${WASM_BASE}/_framework/dotnet.js`;
      const bootConfigUrl = `${WASM_BASE}/_framework/blazor.boot.json`;
      const mod = await import(/* webpackIgnore: true */ dotnetUrl);
      const dotnet = mod.dotnet as
        | {
            withConfigSrc: (src: string) => { create: () => Promise<unknown> };
            create: () => Promise<unknown>;
          }
        | undefined;
      if (!dotnet?.create) {
        throw new Error('Word Connect WASM: dotnet runtime not found.');
      }
      const runtime = dotnet.withConfigSrc
        ? await dotnet.withConfigSrc(bootConfigUrl).create()
        : await dotnet.create();
      return runtime as {
        getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
        getConfig: () => { mainAssemblyName: string };
      };
    })();
  }

  const runtime = await dotnetModulePromise;
  const config = runtime.getConfig();
  return loadWordConnectAssemblyExports(runtime.getAssemblyExports, config.mainAssemblyName);
}

export function useWordConnectWasm(engineBootstrapCipher: string | undefined) {
  const exportsRef = useRef<WordConnectWasmExports | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!engineBootstrapCipher?.trim()) {
      setReady(false);
      setError(null);
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        const wasm = await loadWasmExports();
        if (cancelled) return;
        const keyB64 = wasm.getBootstrapKeyBase64();
        const plainB64 = await decryptWordConnectBootstrapCipher(engineBootstrapCipher, keyB64);
        if (cancelled) return;
        const initResult = wasm.initializeSessionFromPlaintext(plainB64);
        const initError =
          typeof initResult === 'string' ? initResult.trim() : 'Failed to initialize Word Connect engine.';
        if (initError) {
          setError(initError);
          setReady(false);
          return;
        }
        exportsRef.current = wasm;
        setError(null);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Word Connect WASM load failed.');
        setReady(false);
      }
    })();

    return () => {
      cancelled = true;
      exportsRef.current?.disposeSession();
      exportsRef.current = null;
      setReady(false);
    };
  }, [engineBootstrapCipher]);

  const tryPlaceLetter = useCallback(
    async (slotId: string, row: number, col: number): Promise<WordConnectPlayState> => {
      const wasm = exportsRef.current;
      if (!wasm) {
        return parseWordConnectPlayState({
          placementAccepted: false,
          placementMessage: 'Engine not ready.',
        });
      }
      const json = wasm.tryPlaceLetter(slotId, row, col);
      return parseWordConnectPlayState(JSON.parse(json));
    },
    []
  );

  const buildFinishPayload = useCallback((): WordConnectFinishAuditPayload | null => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.buildFinishPayload();
    if (!json || json === '{}') return null;
    return JSON.parse(json) as WordConnectFinishAuditPayload;
  }, []);

  const disposeSession = useCallback(() => {
    exportsRef.current?.disposeSession();
    exportsRef.current = null;
    setReady(false);
  }, []);

  return {
    ready,
    error,
    tryPlaceLetter,
    buildFinishPayload,
    disposeSession,
  };
}
